// Intel types matching backend IntelReportDto and SF-85 data structures

export type IntelType = 'CB' | 'SOM' | 'SURVEY' | 'INFILTRATE' | 'SOS' | 'SOHD' | 'SOT' | 'CE';

export type SharingStatus = 'PRIVATE' | 'SHARED' | 'AUTO_SHARED';

// Backend API response types
export interface IntelListResponse {
  reports: IntelReportDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface IntelReportDto {
  id: string;
  observerProvinceId: string;
  observerKingdomId: string;
  targetProvinceId: string | null;
  targetProvinceName: string;
  targetKingdomId: string;
  targetKingdomName: string;
  targetKingdomLocation: string;
  type: IntelType;
  capturedAt: string;
  capturedAtTick: number;
  sharing: SharingStatus;
  // Backend uses @JsonRawValue which outputs raw JSON (not quoted string),
  // so HTTP client parses it as an object. Could be string for backwards compatibility.
  data: Record<string, unknown> | string;
}

// Client-side grouping types
export interface KingdomGroup {
  kingdomId: string;
  kingdomName: string;
  kingdomLocation: string;
  atWar: boolean;
  provinces: ProvinceGroup[];
}

export interface ProvinceGroup {
  provinceId: string | null;
  provinceName: string;
  intel: IntelReportDto[];
}

// Crystal Ball data - matches backend CrystalBallData.java
export interface CrystalBallData {
  race: string;
  personality: string;
  rulerName: string;
  totalLand: number;
  buildingEfficiency: number;
  money: number;
  food: number;
  runes: number;
  tradeBalance: number;
  peasants: number;
  soldiers: number;
  offenseSpecialists: number;
  defenseSpecialists: number;
  elites: number;
  warHorses: number;
  prisoners: number;
  offensePoints: number;
  defensePoints: number;
  honour: number;
  title: string;
  activeEffects: ActiveEffect[];
  kingdomStance: string;
  dragonType: string;
  atWarWith: string[];
  // SF-157: frozen at gather time, nullable — absent on reports persisted before SF-157 shipped.
  networth?: number | null;
}

export interface ActiveEffect {
  effectType: string;
  ticksRemaining: number;
  appliedAtTick: number;
  indefinite: boolean;
}

// Generic low/high pair type — used by MilitaryMath range displays in the Dossier.
// Intel records themselves use exact values after SF-85.
export interface FuzzyValue {
  low: number;
  high: number;
}

// Spy on Military data - exact values, no fuzz (SF-85)
export interface SpyOnMilitaryData {
  soldiers: number;
  defensiveSpecs: number;
  offensiveSpecs: number;
  elites: number;
  thieves: number;
  generals: number | null;
  hasTrainingQueue: boolean;
  queueLength: number;
  nextCompletionTicks: number | null;
  hasArmiesOut: boolean;
  armyCount: number;
  nextReturnTicks: number | null;
  defensiveME: number;
  offensiveME: number | null;
  offensePoints: number | null;
  defensePoints: number | null;
  race: string | null;
  // SF-170: per-unit-type incoming-troop breakdown, aggregated from Province.getTrainings().
  // Nullable/optional — additive field, absent on reports persisted before SF-170 shipped.
  // SF-171's MobileSpyOnMilitaryCard renders its Training Queue section only when this is
  // present/non-null, omitting the section entirely otherwise.
  trainingUnits?: TrainingBatch[] | null;
  // SF-157: frozen at gather time, nullable — absent on reports persisted before SF-157 shipped.
  networth?: number | null;
}

// SF-170: nested per-unit-type training breakdown (mirrors SurveyData.Construction's nested-
// record convention). unitCount/ticksRemaining match backend's double/int typing verbatim.
export interface TrainingBatch {
  unitType: string;
  unitCount: number;
  ticksRemaining: number;
}

// Survey data - exact values, no fuzz (SF-85)
export interface SurveyData {
  totalLand: number;
  barrenLand: number;
  buildings: Record<string, number>;
  construction: Construction[];
  exploration: FuzzyExploration[];
  // SF-157: frozen at gather time, nullable — absent on reports persisted before SF-157 shipped.
  networth?: number | null;
}

// Construction queue entry with exact values (SF-85)
export interface Construction {
  buildingType: string;
  acres: number;
  ticksRemaining: number;
}

// Matches backend FuzzyExploration record (kept for compatibility)
export interface FuzzyExploration {
  acres: number;
  ticksRemaining: number;
}

// Infiltrate data - exact values (SF-85)
export interface InfiltrateData {
  thieves: number;
  rawTPA: number;
  // SF-157: frozen at gather time, nullable — absent on reports persisted before SF-157 shipped.
  networth?: number | null;
}

// Spy on Home Defense data - matches backend SpyOnHomeDefenseData record (SF-84)
export interface SpyOnHomeDefenseData {
  homeDefense: number;
  // SF-157: frozen at gather time, nullable — absent on reports persisted before SF-157 shipped.
  networth?: number | null;
}

// Spy on Throne data - CB-hidden fields, exact values (SF-85)
export interface SpyOnThroneData {
  ruler: string;
  race: string;
  thieves: number;
  wizards: number;
  manaRating: number;
  netIncomePerTick: number;
  // SF-157: frozen at gather time, nullable — absent on reports persisted before SF-157 shipped.
  networth?: number | null;
}

// Spy on Science data - exact values (SF-85)
export interface SpyOnScienceData {
  researchRate: string;
  sciences: ScienceProgress[];
  // SF-157: frozen at gather time, nullable — absent on reports persisted before SF-157 shipped.
  networth?: number | null;
}

// SF-91d: Crystal Eye data - kingdom event log for the lookback window (48 ticks)
export interface CrystalEyeData {
  kingdomId: string;
  kingdomName: string;
  location: string;
  fromTick: number;
  toTick: number;
  events: KingdomEventEntry[];
}

export interface KingdomEventEntry {
  tick: number;
  category: string;          // ATTACK / AID / WAR / RELATION / DRAGON / DEATH
  summary: string;           // pre-rendered text
  actorProvince: string | null;
  actorKingdom: string | null;
  targetProvince: string | null;
  targetKingdom: string | null;
}

export interface ScienceProgress {
  scienceType: string;
  booksKnown: number;
  effectPercentage: number;
}

// Discriminated union for type-safe parsed data
export type IntelDetail =
  | { type: 'CB'; data: CrystalBallData }
  | { type: 'SOM'; data: SpyOnMilitaryData }
  | { type: 'SURVEY'; data: SurveyData }
  | { type: 'INFILTRATE'; data: InfiltrateData }
  | { type: 'SOS'; data: SpyOnScienceData }
  | { type: 'SOHD'; data: SpyOnHomeDefenseData }
  | { type: 'SOT'; data: SpyOnThroneData }
  | { type: 'CE'; data: CrystalEyeData };

// Parse intel data - handles both string and pre-parsed object formats
// Backend uses @JsonRawValue which outputs data as raw JSON (not a quoted string),
// so the API response has data already parsed as a JavaScript object
export function parseIntelData(report: IntelReportDto): IntelDetail {
  const parsedData = typeof report.data === 'string' ? JSON.parse(report.data) : report.data;

  switch (report.type) {
    case 'CB':
      return { type: 'CB', data: parsedData as CrystalBallData };
    case 'SOM':
      return { type: 'SOM', data: parsedData as SpyOnMilitaryData };
    case 'SURVEY':
      return { type: 'SURVEY', data: parsedData as SurveyData };
    case 'INFILTRATE':
      return { type: 'INFILTRATE', data: parsedData as InfiltrateData };
    case 'SOS':
      return { type: 'SOS', data: parsedData as SpyOnScienceData };
    case 'SOHD':
      return { type: 'SOHD', data: parsedData as SpyOnHomeDefenseData };
    case 'SOT':
      return { type: 'SOT', data: parsedData as SpyOnThroneData };
    case 'CE':
      return { type: 'CE', data: parsedData as CrystalEyeData };
  }
}

// Filter types
export interface IntelFilters {
  scope: 'mine' | 'kingdom';
  types: Set<IntelType>;
  kingdomSearch: string;
  sort: 'newest' | 'oldest';
}
